import { Box, Checkbox, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import * as React from 'react';
import { ModalBase } from './ModalBase';
import { ContextHelper } from './ContextHelper';
import { InputBaseSx, SelectMenuItemSx, SelectMenuProps } from '../@shared/templates';
import { Palette, Sizes } from '../@shared/palette';
import { FlattenedSchemaItem, SchemaApiValue, StructureItemType } from '../@shared/Models/Models';
import { isOptionalSchemaItem } from '../@shared/schemaUtils';
import { useCollectDataItem } from '../hooks/useCollectDataItem';

export type CollectDataItemModalProps = {
  isOpen: boolean;
  schema: SchemaApiValue;
  onClose: () => void;
}

const getLabel = (field: FlattenedSchemaItem) => {
    switch (field.schemaItem.valueTypeId) {
        case StructureItemType.NumberArray:
            return `${field.key} (comma separated list of ${field.schemaItem.arrayLength} numbers)`;
        case StructureItemType.Number:
            return `${field.key} (number)`;
        default:
            return `${field.key}`;
    }
}

export const CollectDataItemModal = (props: CollectDataItemModalProps) => {
  const { fields, values, errors, isForMetrics, isSaving, onChangeValue, onChangeIsForMetrics, onSave } =
    useCollectDataItem(props.schema, props.isOpen, props.onClose);

  return <ModalBase
    isOpen={props.isOpen}
    onCancel={props.onClose}
    onSave={onSave}
    title="Collect item"
    isProcessing={isSaving}
    isDisabledSave={isSaving || fields.length === 0}
    contextHelperContent={<>A single data item is stored separately and is merged into the dataset automatically. Every field matches a feature or info item of the schema.</>}
  >
      <ContextHelper content={<>When enabled, the item is added to the testing dataset which is used to evaluate the model instead of training it.</>}>
          <FormControlLabel
              sx={{ marginTop: "10px", marginBottom: "10px", marginLeft: "0px" }}
              control={<Checkbox checked={isForMetrics} sx={{ padding: 0, marginRight: "10px" }} onChange={(_, value) => onChangeIsForMetrics(value)} />}
              label={<Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>Add to metrics dataset</Typography>}
          />
      </ContextHelper>
      <Box>
        {fields.map(field => {
          const error = errors[field.key];
          const isCategory = field.schemaItem.valueTypeId === StructureItemType.Category;

          return <TextField
            key={field.key}
            sx={InputBaseSx}
            fullWidth
            size="small"
            variant="outlined"
            select={isCategory}
            required={!isOptionalSchemaItem(field.schemaItem)}
            type={field.schemaItem.valueTypeId === StructureItemType.Number ? "number" : "text"}
            label={getLabel(field)}
            value={values[field.key] ?? ""}
            onChange={(e) => onChangeValue(field.key, e.target.value)}
            error={!!error}
            slotProps={isCategory ? { select: { MenuProps: SelectMenuProps } } : undefined}
          >
            {isCategory && (field.schemaItem.categories ?? []).map(category =>
              <MenuItem key={category.value} sx={SelectMenuItemSx} value={category.value}>{category.value}</MenuItem>
            )}
          </TextField>
        })}
      </Box>
  </ModalBase>
}
